import {
  JsonRpcProvider,
  JsonRpcSigner,
  TransactionRequest,
  TransactionResponse,
  TypedDataDomain,
  TypedDataField,
  getAddress,
  defineProperties,
  JsonRpcApiProvider,
} from 'ethers';

import { ConnectSuccessData, GetWalletAPI } from '../WalletAPI';
import { ChainInfo } from '../ChainInfo';

type WebViewCallback = (bridge: any) => void;

declare global {
  interface Window {
    WKWebViewJavascriptBridge?: any; // Bridge for WKWebView
    WKWVJBCallbacks?: WebViewCallback[]; // Callbacks for WKWebView
    webkit: {
      messageHandlers: {
        iOS_Native_InjectJavascript: {
          postMessage: (message: any) => void;
        };
      };
    };
  }
}

type BridgeResponse =
  | {
      success: string;
    }
  | {
      error: string;
    };

type BridgeCallParams = {
  type: string;
  requestData: any;
  requestId: string;
};

function setupWKWebViewJavascriptBridge(callback: any): void {
  if (window.WKWebViewJavascriptBridge !== undefined) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    callback(window.WKWebViewJavascriptBridge);
  }
  if (window.WKWVJBCallbacks) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    window.WKWVJBCallbacks.push(callback);
  } else {
    window.WKWVJBCallbacks = [callback];
  }

  window.webkit.messageHandlers.iOS_Native_InjectJavascript.postMessage(null);
}

class CustomJsonRpcSigner extends JsonRpcSigner {
  // eslint-disable-next-line @typescript-eslint/ban-types
  private callbacks: Record<string, Function> = {};

  address!: string;

  constructor(provider: JsonRpcApiProvider, address: string) {
    super(provider, address);

    defineProperties<JsonRpcSigner>(this, { address: getAddress(address) });
  }

  private handleBridgeResponse = (
    response: BridgeResponse,
    requestId: string,
    type: string,
  ): void => {
    if ('error' in response) {
      const failCallback = this.callbacks[`${requestId}_fail`];
      if (failCallback !== undefined) {
        failCallback(response.error);
        delete this.callbacks[`${requestId}_fail`];
      }
    } else {
      const callback = this.callbacks[requestId];
      if (callback !== undefined) {
        if (type === 'sendTransaction') {
          callback({
            hash: response.success,
          });
        } else {
          callback(response.success);
        }
        delete this.callbacks[requestId];
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private bridgeCall({ type, requestData, requestId }: BridgeCallParams) {
    setupWKWebViewJavascriptBridge((bridge: any): void => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      bridge.callHandler(type, requestData, (response: BridgeResponse) =>
        this.handleBridgeResponse(response, requestId, type),
      );
    });
  }

  signMessage(message: Uint8Array): Promise<string> {
    const requestId = `${Date.now()}-${Math.random()}`;
    const messageString = Buffer.from(message).toString('hex');

    const promise = new Promise<string>((resolve, reject) => {
      this.callbacks[requestId] = resolve;
      this.callbacks[`${requestId}_fail`] = reject;
    });

    this.bridgeCall({
      type: 'signMessage',
      requestData: { message: messageString },
      requestId,
    });

    return promise;
  }

  sendTransaction(tx: TransactionRequest): Promise<TransactionResponse> {
    const requestId = `${Date.now()}-${Math.random()}`;

    const promise = new Promise<TransactionResponse>((resolve, reject) => {
      this.callbacks[requestId] = resolve;
      this.callbacks[`${requestId}_fail`] = reject;
    });

    this.bridgeCall({
      type: 'sendTransaction',
      requestData: { transaction: tx },
      requestId,
    });

    return promise;
  }

  signTypedData(
    domain: TypedDataDomain,
    types: Record<string, Array<TypedDataField>>,
    value: Record<string, any>,
  ): Promise<string> {
    const requestId = `${Date.now()}-${Math.random()}`;
    const typedData = { domain, types, value };

    const promise = new Promise<string>((resolve, reject) => {
      this.callbacks[requestId] = resolve;
      this.callbacks[`${requestId}_fail`] = reject;
    });

    this.bridgeCall({
      type: 'signTypedData',
      requestData: { typedData },
      requestId,
    });

    return promise;
  }

  getAddress(): Promise<string> {
    return new Promise((resolve) => {
      resolve(this.address);
    });
  }
}

export function iosAppAPI({
  addressGetter,
}: {
  addressGetter: () => string | undefined;
}): GetWalletAPI {
  return ({ type }) => {
    const address = addressGetter() ?? '';
    let currentChainInfo: ChainInfo;

    return {
      type,
      getSigner: (): Promise<JsonRpcSigner | undefined> =>
        new Promise((resolve, reject) => {
          const provider = new JsonRpcProvider(currentChainInfo?.rpc);
          const signer = new CustomJsonRpcSigner(provider, address);

          resolve(signer);
        }),
      connect: ({ chainInfo }): Promise<ConnectSuccessData> => {
        const chainId = parseInt(chainInfo.chainId, 10);
        currentChainInfo = chainInfo;

        if (address === undefined || !chainId) {
          throw new Error(
            'Wallet address or chainId is undefined. Try to reload the page',
          );
        }

        return Promise.resolve({
          address,
          chainId,
        });
      },
    };
  };
}
